import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FriendCountsProvider } from '../../context/FriendCountsContext';
import EventCard from '../EventCard';
import { formatDescription } from '../../utils/formatDescription';

function normalizeVenueWebsiteUrl(url) {
  const s = String(url || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
}

function VenueDetailsContent({
  venue,
  venueEvents = [],
  onClose,
  openEvent,
  fromEventId,
  onBackToEvent,
  timeZone = 'America/New_York',
  sheTheyForwardTimeline = false,
}) {
  const contentRef = useRef(null);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const stripHtml = (html = '') => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  useEffect(() => {
    contentRef.current?.scrollTo(0, 0);
  }, [venue?.id]);

  useEffect(() => {
    setShowFullDescription(false);
  }, [venue?.id]);

  const venueFriendCountEventIds = useMemo(
    () => (venueEvents || []).map((e) => e.id).filter((id) => id != null),
    [venueEvents]
  );

  const { groupedEvents, sectionOrder } = useMemo(() => {
    const sortedEvents = [...venueEvents].sort(
      (a, b) =>
        new Date(a.start_time || a.formatted_start_time) -
        new Date(b.start_time || b.formatted_start_time)
    );

    const childVenues = venue?.child_venues || [];
    const parentSectionLabel = venue?.parent_section_label ?? venue?.display_venue_for_json?.parent_section_label;

    const groups = sortedEvents.reduce((acc, event) => {
      const dayLabel = new Date(
        event.start_time || event.formatted_start_time
      ).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      });

      if (!acc[dayLabel]) acc[dayLabel] = {};

      const venueId = event.venue?.id;
      let subLabel = '_';
      if (childVenues.length) {
        const child = childVenues.find((c) => c.id === venueId);
        if (child) {
          subLabel = child.subheading || child.name;
        } else {
          subLabel = parentSectionLabel || venue?.name || '';
        }
      }

      if (!acc[dayLabel][subLabel]) acc[dayLabel][subLabel] = [];
      acc[dayLabel][subLabel].push(event);
      return acc;
    }, {});

    // Fixed order: main venue first, then child venues in array order (only sections that exist)
    const order =
      childVenues.length > 0
        ? [parentSectionLabel || venue?.name || ''].concat(
            childVenues.map((c) => c.subheading || c.name)
          )
        : null;

    return { groupedEvents: groups, sectionOrder: order };
  }, [venueEvents, venue?.child_venues, venue?.parent_section_label, venue?.display_venue_for_json?.parent_section_label, venue?.name]);
  if (!venue) {
    return (
      <div ref={contentRef} className="event-details-content">
        <button className="event-details-close" onClick={onClose} aria-label="Close venue details">
          ×
        </button>
        <p>Venue not found.</p>
      </div>
    );
  }

  const {
    name,
    logo_url,
    address,
    location,
    venue_type,
    subheading,
    description,
    age,
    venue_url,
  } = venue;

  const previewLength = 145;
  const plainDescription = stripHtml(description || '');
  const isLongDescription = plainDescription.length > previewLength;
  const previewDescription = isLongDescription
    ? `${plainDescription.slice(0, previewLength).trim()}...`
    : plainDescription;

  const displayVenueType =
    name === 'Joia Beach' || name === 'National Hotel' || name === 'Tala Beach'
      ? 'Beachclub'
      : venue_type;

  return (
    <FriendCountsProvider eventIds={venueFriendCountEventIds}>
    <div
      ref={contentRef}
      className="event-details-content venue-details-content"
      style={{ minHeight: '100%' }}
    >
      <div className="event-details-header flex mb-sm">
        {fromEventId && onBackToEvent && (
          <button
            type="button"
            className="event-details-back"
            onClick={onBackToEvent}
            aria-label="Back to event"
          >
            <i className="fa-solid fa-arrow-left" aria-hidden />
            Back to event
          </button>
        )}
        <button className="event-details-close" onClick={onClose} aria-label="Close venue details">
          ×
        </button>
      </div>

      {logo_url && venue_type !== 'Pool' && venue_type !== 'Boat' && (
        <img
          src={logo_url}
          alt={name}
          className="event-details-image"
        />
      )}

      {(
        name === 'Joia Beach' ||
        name === 'National Hotel' ||
        name === 'Tala Beach'
      ) && (
        <img
          src={logo_url}
          alt={name}
          className="event-details-image"
        />
      )}

      {!logo_url && (
        <h2>{name}</h2>
      )}

      {venue?.child_venues?.length > 0 && (
        <div className="venue-child-logos mb-sm">
          {venue.child_venues
            .filter((c) => c.logo_url)
            .map((child) => (
              <img
                key={child.id}
                src={child.logo_url}
                alt={child.subheading || child.name}
                className="venue-child-logo"
              />
            ))}
        </div>
      )}

      {subheading && (
        <h2 className="event-venue mb-xs highlight">
          {subheading}
        </h2>
      )}

      {(location || address) && (
        <p className="event-venue-address mb-xs">
          <i className="fa-solid fa-map-pin"></i>&nbsp;
          {location}
          {location && address && ' - '}
          {address}
        </p>
      )}

      {age && (
        <p className="event-age mb-xs">
          <i className="fa-solid fa-id-card"></i>&nbsp;
          {age}
        </p>
      )}

      {displayVenueType && (
        <p className="event-age mb-xs">
          <i className="fa-solid fa-building"></i>&nbsp;
          {displayVenueType}
        </p>
      )}

      {description && (
        <div className="event-description mb-xs">
          {showFullDescription ? (
            <>
              <div dangerouslySetInnerHTML={{ __html: formatDescription(description) }} />
              {isLongDescription && (
                <button
                  type="button"
                  className="description-inline-toggle"
                  onClick={() => setShowFullDescription(false)}
                >
                  Show less
                </button>
              )}
            </>
          ) : (
            <p className="description-preview">
              {previewDescription}
              {isLongDescription && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="description-inline-toggle"
                    onClick={() => setShowFullDescription(true)}
                  >
                    See full description
                  </button>
                </>
              )}
            </p>
          )}
        </div>
      )}

      {venue_url && (
        <div className="event-details-actions mb-sm">
          <a
            href={normalizeVenueWebsiteUrl(venue_url)}
            target="_blank"
            rel="noreferrer"
            className="button mb-xs"
          >
            Venue website
          </a>
        </div>
      )}

      {venueEvents.length > 0 && (
        <div className="venue-events-list mb-xs">
          {Object.entries(groupedEvents).map(([day, dayGroups]) => {
            const isGroupedBySubVenue = typeof dayGroups === 'object' && !Array.isArray(dayGroups);
            const sections = isGroupedBySubVenue
              ? (sectionOrder
                  ? sectionOrder
                      .filter((label) => dayGroups[label]?.length)
                      .map((label) => [label, dayGroups[label]])
                  : Object.entries(dayGroups))
              : [['_', dayGroups]];

            return (
              <div key={day} className="mb-xl mt-xl venue-event-group mb-sm">
                <h3 className="venue-event-group-title mb-xs">{day}</h3>
                {sections.map(([subLabel, events]) => (
                  <div key={subLabel || 'main'} className="venue-event-subgroup mb-sm">
                    {subLabel !== '_' && (
                      <div className="venue-event-subgroup-header mb-xs">
                        <h5 className="venue-event-subvenue-title mb-xs">{subLabel}</h5>
                      </div>
                    )}
                    <div className="venue-event-cards">
                      {events.map((event) => (
                        <EventCard
                          key={event.id}
                          event={event}
                          timeZone={timeZone}
                          sheTheyForwardTimeline={sheTheyForwardTimeline}
                          onClick={() => openEvent?.(event.id, venue?.id)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
    </FriendCountsProvider>
  );
}

export default VenueDetailsContent;
